import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import dayjs from 'dayjs';

import Column from 'components/common/Column';
import CommonImage from 'components/common/CommonImage';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import SettlementBadge from 'containers/SettlementRequest/components/SettlementBadge';
import { SettlementsNode } from 'containers/SettlementRequest/graphQL/types';
import {
  ICON_ARROW_GRAY_BOLD,
  ICON_TERM_CHECKED_ABLE,
  ICON_TERM_CHECKED_DISABLE,
} from 'assets/icons';
import AuthButton from 'components/Button/AuthButton';
import { SETTLEMENT_DETAIL_PATH_DEFAULT } from 'constants/path';
import SettlementRequestModal from 'containers/SettlementRequest/components/SettlementRequestModal';
import HackleManager from 'utils/hackle';
import { useMutaionRequestInvoice } from 'containers/SettlementRequest/graphQL/hooks/useMutationRequestInvoice';

const Container = styled.div<{ $isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  padding: 16px 20px;
  width: 100%;

  border-radius: 12px;
  background: var(--gray-gray-01, #f7f7f8);
  border: ${props => (props.$isSelected ? '1.5px solid #1b1c1e' : 'none')};
`;

interface SettlementCardIProps {
  isMobile: boolean;
  settlement: SettlementsNode;
  selectedSettlement: SettlementsNode[];
  isHaveMinusSettlement: boolean;
  handleSelectSettlement: (settlement: SettlementsNode) => void;
  refetchSettlementList: () => void;
}

const SettlementCard: React.FC<SettlementCardIProps> = props => {
  const {
    isMobile,
    settlement,
    selectedSettlement,
    isHaveMinusSettlement,
    handleSelectSettlement,
    refetchSettlementList,
  } = props;

  const navigate = useNavigate();

  const { handleRequestInvoice } = useMutaionRequestInvoice();

  const [isOpenSettlementInformation, setIsOpenSettlementInformation] =
    useState<boolean>(false);
  const [isOpenSettlementRequestModal, setIsOpenSettlementRequestModal] =
    useState<boolean>(false);

  const isMinusSettlement =
    Number(settlement.totalPayment.value) + Number(settlement.totalFee.value) <
    0;
  const isIncludeSettlement = selectedSettlement.includes(settlement);

  const handleRequestInvoiceSettlement = (id: string) => {
    handleRequestInvoice(id, data => {
      const settlementResponse = data.requestInvoice.settlements[0];

      refetchSettlementList();

      HackleManager.logSettlementListPayoutRequestComplete(
        settlementResponse.id,
        settlementResponse.title,
        Number(settlementResponse.totalFee.value) +
          Number(settlementResponse.totalPayment.value),
        Number(settlementResponse.totalFee.value),
        Number(settlementResponse.totalPayment.value),
      );
    });
  };

  const renderContents = (): JSX.Element => {
    return (
      <Column gap="16px" align="flex-start">
        <Row justifyContent="space-between" style={{ width: '100%' }}>
          <Row gap="8px">
            <SettlementBadge status={settlement.invoice?.status} />
            <Column align="flex-start" style={{ width: 'auto' }}>
              <Text color="#1b1c1e" size="16px" weight={600}>
                {settlement.title}
              </Text>
              <Text color="#70737C" size="13px" weight={400}>
                {dayjs(settlement.startedDate).format('YYYY-MM-DD')} ~{' '}
                {dayjs(settlement.endedDate).format('YYYY-MM-DD')}
              </Text>
            </Column>
          </Row>
          {settlement.invoice === null && (
            <Row gap="2px" onClick={() => handleSelectSettlement(settlement)}>
              <CommonImage
                src={
                  isIncludeSettlement
                    ? ICON_TERM_CHECKED_ABLE
                    : ICON_TERM_CHECKED_DISABLE
                }
                onClick={() => handleSelectSettlement(settlement)}
              />
              <Text
                color={isIncludeSettlement ? '#1b1c1e' : '#c2c4c8'}
                weight={400}
              >
                선택
              </Text>
            </Row>
          )}
        </Row>
        <Text
          color={isMinusSettlement ? '#FF4242' : '#1b1c1e'}
          size="20px"
          weight={600}
        >
          {(
            Number(settlement.totalPayment.value) +
            Number(settlement.totalFee.value)
          ).toLocaleString()}
          원
        </Text>
      </Column>
    );
  };

  const renderBottomSheet = (): JSX.Element => {
    return (
      <Row gap="24px" justifyContent="space-between" style={{ width: '100%' }}>
        <Row gap="6px">
          <Text
            color="#70737C"
            size="13px"
            style={{ cursor: 'pointer' }}
            onClick={() => setIsOpenSettlementInformation(true)}
          >
            정산 미리보기
          </Text>
          <Text color="#DBDCDF">|</Text>
          <Text
            color="#70737C"
            size="13px"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              navigate(`${SETTLEMENT_DETAIL_PATH_DEFAULT}/${settlement.id}`)
            }
          >
            정산 내역
          </Text>
          <CommonImage src={ICON_ARROW_GRAY_BOLD} onClick={() => {}} />
        </Row>
        {settlement.invoice === null && (
          <AuthButton
            name="지급 요청"
            isAbled={!isHaveMinusSettlement}
            onClick={() => {
              setIsOpenSettlementRequestModal(true);
            }}
            style={{ padding: '10px 16px', width: 'auto' }}
          />
        )}
      </Row>
    );
  };

  return (
    <Container $isSelected={isIncludeSettlement}>
      {renderContents()}
      {selectedSettlement.length === 0 && renderBottomSheet()}
      {isOpenSettlementInformation && (
        <SettlementRequestModal
          isMobile={isMobile}
          settlementItem={settlement}
          type="information"
          onClose={() => setIsOpenSettlementInformation(false)}
        />
      )}
      {isOpenSettlementRequestModal && (
        <SettlementRequestModal
          isMobile={isMobile}
          type="request"
          settlementItem={settlement}
          handleRequestInvoice={handleRequestInvoiceSettlement}
          onClose={() => setIsOpenSettlementRequestModal(false)}
        />
      )}
    </Container>
  );
};

export default SettlementCard;
